import React, { useCallback, useEffect, useRef, useState } from 'react'

import { Add } from '@mui/icons-material'
import { Alert, Box, Divider, Grid, SxProps, Theme, Typography } from '@mui/material'

import { useSnackbar } from '../../shared/components/Snackbar/context/SnackBar.context.container'

import Button from '../../shared/components/Button/Button.component'
import Overview from '../../shared/components/Overview/Overview.component'
import PaymentDetails from '../../shared/components/PaymentDetails/PaymentDetails.component'
import { PaymentSubmittedSuccessfully } from '../../shared/components/PaymentSubmittedSuccessfully/PaymentSubmittedSuccessfully.component'
import ResponsiveDisplayWrapper from '../../shared/components/ResponsiveDisplayWrapper/ResponsiveDisplayWrapper.component'
import Stepper from '../../shared/components/Stepper/Stepper.component'
import USPSLocationFinder from '../../shared/components/USPSLocationFinder/USPSLocationFinder.component'
import AddItemForm from './components/AddItemForm/AddItemForm.component'
import CustomsLimitErrorModal from './components/CustomsLimitError/CustomsLimitError.component'
import FulfillmentRecipientCard from './components/FulfillmentRecipientCard/FulfillmentRecipientCard.component'
import OverallOrderDetails from './components/OverallOrderDetails/OverallOrderDetails.component'
import RecipientDetails from './components/RecipientDetails/RecipientDetails.component'

import { getCountriesAndCities } from '../../redux/states/flights/getCountriesAndCities/getCountriesAndCities.slice'
import {
  createFulfillmentOrder,
  resetCreateFulfillmentOrder
} from '../../redux/states/fulfillmentOrders/createFulfillmentOrder/createFulfillmentOrder.slice'
import { FulfillmentOrderPackage } from '../../redux/states/fulfillmentOrders/createFulfillmentOrder/createFulfillmentOrder.types'
import {
  createFulfillmentOrderPaymentIntent,
  resetCreateFulfillmentOrderPaymentIntent
} from '../../redux/states/payments/createFulfillmentOrderPaymentIntent/createFulfillmentOrderPaymentIntent.slice'
import { CreateFulfillmentOrderPaymentIntentRequestPayload } from '../../redux/states/payments/createFulfillmentOrderPaymentIntent/createFulfillmentOrderPaymentIntent.types'
import { UspsLocation } from '../../redux/states/usps/getUspsLocations/getUspsLocations.types'
import {
  resetValidateUspsAddress,
  validateUspsAddress
} from '../../redux/states/usps/validateUspsAddress/validateUspsAddress.slice'
import { ValidateUspsAddressRequestPayload } from '../../redux/states/usps/validateUspsAddress/validateUspsAddress.types'
import { RootState } from '../../redux/store/store.types'
import router from '../../router/functions/router.functions'
import routes from '../../router/routes.dictionary'
import { PaymentFormRef } from '../../shared/components/StripeCheckoutForm/StripeCheckoutForm.types'
import {
  buildUspsZipCodeForAddressRequest,
  defaultRadiusMiles,
  isUnitedStatesPostalCodeOrZipPlus4
} from '../../shared/components/USPSLocationFinder/USPSLocationFinder.functions'
import { stripPrefixFromId } from '../../shared/functions/ID/id.functions'
import { getCountryByCode } from '../../shared/functions/Places/places.functions'
import { useAppDispatch, useAppSelector } from '../../shared/hooks/redux.hooks'
import useResponsiveness from '../../shared/hooks/responsive.hooks'
import { FULFILLMENT_ORDER_SUBMISSION_KEY, submissionSteps } from './CreateFulfillmentOrder.dictionary'
import styles from './CreateFulfillmentOrder.styles'
import {
  CustomsLimitError,
  FulfillmentOrderItem,
  OverallPackageDetails,
  RecipientDetailsValues
} from './CreateFulfillmentOrder.types'
import { parseZipPlusFourValidationSignal } from './components/RecipientDetails/uspsValidationError.functions'

function uspsLocationToRecipientAddressPart(
  location: UspsLocation
): Pick<RecipientDetailsValues, 'streetAddress' | 'city' | 'stateProvince' | 'postalCode' | 'country'> {
  return {
    streetAddress: location.address,
    city: location.city,
    stateProvince: location.state,
    postalCode: buildUspsZipCodeForAddressRequest(location),
    country: 'US'
  }
}

const generateItemFormId = () => `item-form-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`

const CreateFulfillmentOrder: React.FC = () => {
  const [items, setItems] = useState<FulfillmentOrderItem[]>([])
  const [itemFormIds, setItemFormIds] = useState<string[]>(() => [generateItemFormId()])
  const [itemFormValidity, setItemFormValidity] = useState<Record<string, boolean>>({})
  const [packageDetails, setPackageDetails] = useState<FulfillmentOrderPackage | null>(null)
  const [isPackageDetailsValid, setIsPackageDetailsValid] = useState(false)
  const [activeStep, setActiveStep] = useState(0)
  const [recipientDetails, setRecipientDetails] = useState<RecipientDetailsValues | null>(null)
  const [confirmedRecipientDetails, setConfirmedRecipientDetails] = useState<RecipientDetailsValues | null>(null)
  const [isRecipientFormValid, setIsRecipientFormValid] = useState(false)
  const [shouldValidateRecipientForm, setShouldValidateRecipientForm] = useState(false)
  const [isPaymentFormValid, setIsPaymentFormValid] = useState(false)
  const [paymentRequestLoading, setPaymentRequestLoading] = useState(false)
  const [isDesktop] = useResponsiveness()
  const [itemsStepEditHandler] = useState<((index: number) => void) | null>(null)
  const [selectedUSPSLocation, setSelectedUSPSLocation] = useState<{
    locationID: string
    locationData: UspsLocation
  } | null>(null)
  const [useUsPSLocationMode, setUseUsPSLocationMode] = useState(false)
  const [savedRecipientDetailsBeforeUsps, setSavedRecipientDetailsBeforeUsps] = useState<RecipientDetailsValues | null>(
    null
  )
  const [enforceZipPlus4, setEnforceZipPlus4] = useState(false)
  const [suggestedZipPlus4, setSuggestedZipPlus4] = useState<string | null>(null)

  // Error state management
  const [customsError, setCustomsError] = useState<CustomsLimitError | null>(null)
  const [customsErrorAlert, setCustomsErrorAlert] = useState<string | null>(null)

  const dispatch = useAppDispatch()
  const navigate = router.navigate()
  const { show: showSnackbar } = useSnackbar()
  const paymentFormRef = useRef<PaymentFormRef>(null)
  // Track the last processed error to prevent re-processing
  const lastProcessedErrorRef = useRef<string | null>(null)

  // Payment success detection from URL params (Cart pattern)
  const paymentIntentParam = router.getUrlParam('payment_intent')
  const redirectStatusFromPayment = router.getUrlParam('redirect_status')
  const paymentSuccess = redirectStatusFromPayment === 'succeeded' && paymentIntentParam !== null
  const paymentFailure =
    redirectStatusFromPayment && redirectStatusFromPayment !== 'succeeded' && paymentIntentParam !== null

  // Redux state
  const {
    loading: paymentIntentLoading,
    success: paymentIntentSuccess,
    error: paymentIntentError
  } = useAppSelector((state: RootState) => state.createFulfillmentOrderPaymentIntent)
  const { loading: validateAddressLoading } = useAppSelector((state: RootState) => state.validateUspsAddress)
  const {
    loading: orderLoading,
    error: orderError,
    success: orderSuccess
  } = useAppSelector((state: RootState) => state.createFulfillmentOrder)
  const countriesAndCitiesData = useAppSelector((state: RootState) => state.getCountriesAndCities?.success?.data)

  const order = orderSuccess?.data || null

  // Fetch countries and cities on mount
  useEffect(() => {
    if (!countriesAndCitiesData) {
      dispatch(getCountriesAndCities({}))
    }
  }, [dispatch, countriesAndCitiesData])

  // Helper function to convert data URL to File object
  const dataURLtoFile = (dataUrl: string, filename: string): File => {
    const arr = dataUrl.split(',')
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg'
    const bstr = atob(arr[1])
    let n = bstr.length
    const u8arr = new Uint8Array(n)
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n)
    }

    return new File([u8arr], filename, { type: mime })
  }

  // Restore items and recipient from localStorage after redirect (if payment succeeded)
  useEffect(() => {
    if (paymentSuccess && items.length === 0) {
      const orderSubmission: string | null = localStorage.getItem(FULFILLMENT_ORDER_SUBMISSION_KEY)

      if (orderSubmission) {
        try {
          const parsedOrderSubmission = JSON.parse(orderSubmission)

          // Restore recipient details
          if (parsedOrderSubmission.recipient && !confirmedRecipientDetails) {
            setConfirmedRecipientDetails(parsedOrderSubmission.recipient)
          }

          // Restore items with File objects converted from data URLs
          if (parsedOrderSubmission.items && parsedOrderSubmission.items.length > 0) {
            interface StoredItem {
              photoPreview?: string
              photoName?: string
              description: string
              quantity?: number
              weightGrams?: number
              declaredValueCents: number
              currency?: string
              fragile?: boolean
              repackagingAllowed?: boolean
              specialInstructions?: string
            }

            const restoredItems: FulfillmentOrderItem[] = (parsedOrderSubmission.items as StoredItem[]).map(
              (item: StoredItem, index: number) => ({
                id: `restored-${Date.now()}-${index}`,
                photo: item.photoPreview ? dataURLtoFile(item.photoPreview, item.photoName || 'item-photo.jpg') : null,
                photoPreview: item.photoPreview || '',
                description: item.description,
                quantity: item.quantity ?? 1,
                declaredValue: item.declaredValueCents / 100,
                currency: item.currency || 'USD'
              })
            )

            setItems(restoredItems)
            setItemFormIds(restoredItems.map((restoredItem) => restoredItem.id))
            // eslint-disable-next-line no-console
            console.log('[CreateFulfillmentOrder] Restored items from localStorage:', restoredItems.length)
          }
          // Restore USPS location
          if (parsedOrderSubmission.uspsLocation && !selectedUSPSLocation) {
            setSelectedUSPSLocation(parsedOrderSubmission.uspsLocation)
            // eslint-disable-next-line no-console
            console.log('[CreateFulfillmentOrder] Restored USPS location from localStorage')
          }

          // Restore package details
          if (parsedOrderSubmission.packageDetails && !packageDetails) {
            setPackageDetails(parsedOrderSubmission.packageDetails)
            setIsPackageDetailsValid(true)
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('[CreateFulfillmentOrder] Failed to parse order submission from localStorage:', error)
        }
      }
    }
  }, [paymentSuccess, items.length, confirmedRecipientDetails, packageDetails, selectedUSPSLocation])

  // Map payment intent data for PaymentDetails component
  const paymentIntentData = paymentIntentSuccess?.data
    ? {
        id: paymentIntentSuccess.data.paymentIntentId,
        secret: paymentIntentSuccess.data.clientSecret
      }
    : null

  const customerSessionData = paymentIntentSuccess?.data?.customerSession ?? undefined

  // Map payment intent for overview component
  const paymentIntent = paymentIntentSuccess?.data
    ? {
        deliveryFee: (paymentIntentSuccess.data.totalFeeCents || 0) / 100,
        totalAmount: (paymentIntentSuccess.data.totalFeeCents || 0) / 100
      }
    : null

  const handleSubmitOrder = useCallback(() => {
    const orderSubmission: string | null = localStorage.getItem(FULFILLMENT_ORDER_SUBMISSION_KEY)

    if (!orderSubmission) {
      // eslint-disable-next-line no-console
      console.error('[CreateFulfillmentOrder] No order submission found in localStorage')

      return
    }

    if (!paymentIntentParam) {
      // eslint-disable-next-line no-console
      console.error('[CreateFulfillmentOrder] No payment intent ID found in URL')

      return
    }

    const parsedOrderSubmission = JSON.parse(orderSubmission)

    // After redirect, items state might be empty, so restore from localStorage metadata
    // But File objects can't be restored - we need to check if items state has files
    if (items.length === 0) {
      // eslint-disable-next-line no-console
      console.error(
        '[CreateFulfillmentOrder] Items state is empty after redirect. File objects cannot be restored from localStorage.'
      )
      // TODO: Show error to user that they need to re-upload photos

      return
    }

    // Validate that all items have photos
    const itemsWithFiles = items.map((item, index) => {
      if (!item.photo) {
        // eslint-disable-next-line no-console
        console.error(`[CreateFulfillmentOrder] Item at index ${index} is missing a photo file`)
        throw new Error(`Item at index ${index} is missing a photo file`)
      }

      return {
        imageFile: item.photo,
        quantity: parsedOrderSubmission.items[index]?.quantity ?? item.quantity ?? 1,
        description: parsedOrderSubmission.items[index]?.description || item.description,
        declaredValueCents: parsedOrderSubmission.items[index]?.declaredValueCents || item.declaredValue * 100,
        fragile: parsedOrderSubmission.items[index]?.fragile ?? item.fragile ?? false,
        repackagingAllowed: parsedOrderSubmission.items[index]?.repackagingAllowed ?? item.repackagingAllowed ?? false,
        specialInstructions: parsedOrderSubmission.items[index]?.specialInstructions || item.specialInstructions || null
      }
    })

    const packageDetailsToSubmit = parsedOrderSubmission.packageDetails || packageDetails

    // Use recipient from localStorage if confirmedRecipientDetails is not available (after redirect)
    const recipientToUse = confirmedRecipientDetails || parsedOrderSubmission.recipient

    if (!recipientToUse) {
      // eslint-disable-next-line no-console
      console.error('[CreateFulfillmentOrder] No recipient details available')

      return
    }

    // eslint-disable-next-line no-console
    console.log('[CreateFulfillmentOrder] Submitting order with:', {
      paymentIntentId: paymentIntentParam,
      itemCount: itemsWithFiles.length,
      recipientEmail: recipientToUse.email
    })

    dispatch(
      createFulfillmentOrder({
        request: {
          paymentIntentId: paymentIntentParam,
          package: packageDetailsToSubmit,
          items: itemsWithFiles,
          recipient: recipientToUse
        },
        onSuccess: () => {
          localStorage.removeItem(FULFILLMENT_ORDER_SUBMISSION_KEY)
          window.scrollTo(0, 0)
        }
      })
    )
  }, [dispatch, paymentIntentParam, confirmedRecipientDetails, items, packageDetails])

  // Handle payment success from Stripe redirect
  // This runs after items are restored from localStorage
  useEffect(() => {
    if (paymentSuccess && !orderSuccess && !orderLoading && !orderError && items.length > 0) {
      // eslint-disable-next-line no-console
      console.log('[CreateFulfillmentOrder] Payment succeeded, attempting to submit order...', {
        hasOrderSubmission: !!localStorage.getItem(FULFILLMENT_ORDER_SUBMISSION_KEY),
        paymentIntentParam,
        itemsCount: items.length,
        hasConfirmedRecipient: !!confirmedRecipientDetails
      })

      handleSubmitOrder()
      setActiveStep(3) // Move to success page immediately, show loading while order is created
      window.scrollTo(0, 0)
    }
  }, [
    paymentSuccess,
    handleSubmitOrder,
    orderSuccess,
    orderLoading,
    orderError,
    items.length,
    confirmedRecipientDetails,
    paymentIntentParam
  ])

  // Handle order creation error
  useEffect(() => {
    if (orderError && paymentSuccess) {
      setActiveStep(3) // Show error on success page
      window.scrollTo(0, 0)
      // eslint-disable-next-line no-console
      console.error('Order creation failed:', orderError)
    }
  }, [orderError, paymentSuccess])

  const handleRemoveItem = (index: number) => {
    setItems((previous) => previous.filter((_, i) => i !== index))

    if (paymentIntentError) {
      dispatch(resetCreateFulfillmentOrderPaymentIntent())
    }

    dispatch(resetValidateUspsAddress())
  }

  // Multi-item form handlers for step 0
  const handleItemFormChange = useCallback(
    (itemId: string, isValid: boolean, updatedItem: FulfillmentOrderItem | null) => {
      setItemFormValidity((previous) => ({ ...previous, [itemId]: isValid }))

      setItems((previous) => {
        const existingItemIndex = previous.findIndex((existingItem) => existingItem.id === itemId)

        if (isValid && updatedItem) {
          if (existingItemIndex >= 0) {
            const updatedItems = [...previous]

            updatedItems[existingItemIndex] = updatedItem

            return updatedItems
          }

          return [...previous, updatedItem]
        }

        if (existingItemIndex >= 0) {
          return previous.filter((existingItem) => existingItem.id !== itemId)
        }

        return previous
      })
    },
    []
  )

  const handleAddAnotherItem = useCallback(() => {
    setItemFormIds((previous) => [...previous, generateItemFormId()])
  }, [])

  const handleRemoveItemCard = useCallback(
    (itemId: string) => {
      setItemFormIds((previous) => previous.filter((formId) => formId !== itemId))

      setItemFormValidity((previous) => {
        const updatedValidity = { ...previous }

        delete updatedValidity[itemId]

        return updatedValidity
      })

      setItems((previous) => previous.filter((existingItem) => existingItem.id !== itemId))

      if (paymentIntentError) {
        dispatch(resetCreateFulfillmentOrderPaymentIntent())
      }
    },
    [dispatch, paymentIntentError]
  )

  const lastItemFormId = itemFormIds[itemFormIds.length - 1]
  const isLastItemFormValid = itemFormValidity[lastItemFormId] ?? false

  const handlePackageDetailsChange = useCallback((isValid: boolean, updatedPackageDetails: OverallPackageDetails) => {
    setIsPackageDetailsValid(isValid)

    if (isValid) {
      setPackageDetails(updatedPackageDetails)
    }
  }, [])

  const handleCreateNewOrder = useCallback(() => {
    localStorage.removeItem(FULFILLMENT_ORDER_SUBMISSION_KEY)
    dispatch(resetCreateFulfillmentOrderPaymentIntent())
    dispatch(resetValidateUspsAddress())
    dispatch(resetCreateFulfillmentOrder())

    const freshItemFormId = generateItemFormId()

    setActiveStep(0)
    setItems([])
    setItemFormIds([freshItemFormId])
    setItemFormValidity({})
    setRecipientDetails(null)
    setConfirmedRecipientDetails(null)
    setIsRecipientFormValid(false)
    setShouldValidateRecipientForm(false)
    setIsPaymentFormValid(false)
    setPaymentRequestLoading(false)
    setSelectedUSPSLocation(null)
    setUseUsPSLocationMode(false)
    setSavedRecipientDetailsBeforeUsps(null)
    setCustomsError(null)
    setCustomsErrorAlert(null)
    setPackageDetails(null)
    setIsPackageDetailsValid(false)
    lastProcessedErrorRef.current = null

    navigate(routes.createFulfillmentOrder.path, { replace: true })
  }, [dispatch, navigate])

  /**
   * @deprecated Kept for a possible future re-introduction of the USPS drop-off fallback.
   * Do not reference from UI — the client does not want this surfaced right now.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _handleUseUsPSLocationToggleChange_DEPRECATED = useCallback(
    (checked: boolean) => {
      if (checked) {
        if (
          !recipientDetails ||
          recipientDetails.country !== 'US' ||
          !isUnitedStatesPostalCodeOrZipPlus4(recipientDetails.postalCode)
        ) {
          showSnackbar({
            message: 'Enter a valid US address with a 5-digit ZIP or ZIP+4 postal code to find nearby USPS locations.',
            severity: 'error'
          })

          return
        }

        // Save original Address Details before clearing; restored when toggle is turned off
        setSavedRecipientDetailsBeforeUsps(recipientDetails)

        setRecipientDetails({
          ...recipientDetails,
          streetAddress: '',
          city: '',
          stateProvince: '',
          postalCode: ''
        })
        setSelectedUSPSLocation(null)
        setUseUsPSLocationMode(true)
      } else {
        // Revert Address Details form to the content saved when toggle was turned on
        if (savedRecipientDetailsBeforeUsps) {
          setRecipientDetails(savedRecipientDetailsBeforeUsps)
        }

        setSavedRecipientDetailsBeforeUsps(null)
        setUseUsPSLocationMode(false)
        setSelectedUSPSLocation(null)
      }
    },
    [recipientDetails, savedRecipientDetailsBeforeUsps, showSnackbar]
  )
  // Reference the deprecated handler once so linters treat it as used while it is intentionally detached from the UI.
  void _handleUseUsPSLocationToggleChange_DEPRECATED

  const handleUspsLocationSelect = useCallback(
    (locationID: string, locationData: UspsLocation) => {
      if (!savedRecipientDetailsBeforeUsps) return

      setRecipientDetails({
        ...savedRecipientDetailsBeforeUsps,
        ...uspsLocationToRecipientAddressPart(locationData)
      })
      setSelectedUSPSLocation({ locationID, locationData })
      document.getElementById('recipient-details-title')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    [savedRecipientDetailsBeforeUsps]
  )

  // Recipient form handler
  const handleRecipientDetailsChange = useCallback(
    (isValid: boolean, values: RecipientDetailsValues) => {
      setIsRecipientFormValid(isValid)

      setRecipientDetails((previousRecipientDetails) => {
        const addressFieldsChanged =
          previousRecipientDetails?.streetAddress !== values.streetAddress ||
          previousRecipientDetails?.city !== values.city ||
          previousRecipientDetails?.stateProvince !== values.stateProvince ||
          previousRecipientDetails?.postalCode !== values.postalCode ||
          previousRecipientDetails?.country !== values.country

        if (addressFieldsChanged) {
          dispatch(resetValidateUspsAddress())
          setEnforceZipPlus4(false)
          setSuggestedZipPlus4(null)
        }

        return values
      })

      if (isValid && shouldValidateRecipientForm) {
        setShouldValidateRecipientForm(false)
      }
    },
    [dispatch, shouldValidateRecipientForm]
  )

  const previousUseUsPSLocationModeRef = useRef(false)
  useEffect(() => {
    const justToggledOn = useUsPSLocationMode && !previousUseUsPSLocationModeRef.current
    previousUseUsPSLocationModeRef.current = useUsPSLocationMode

    if (justToggledOn) {
      const timeoutId = setTimeout(() => {
        document.getElementById('usps-locations-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)

      return () => clearTimeout(timeoutId)
    }
  }, [useUsPSLocationMode])

  // Parse payment intent creation errors
  useEffect(() => {
    if (!paymentIntentError) {
      setCustomsError(null)
      lastProcessedErrorRef.current = null

      return
    }

    // Prevent re-processing the same error
    // Only process if this is a new error (different reference or different message)
    interface ErrorDetail {
      description?: string
      issue?: string
    }
    interface PaymentIntentError {
      status?: number
      message?: string
      userFriendlyMessage?: string
      details?: ErrorDetail[]
    }
    const error = paymentIntentError as PaymentIntentError | null
    const errorId = `${error?.status}_${error?.message}_${JSON.stringify(error?.details)}`

    if (lastProcessedErrorRef.current === errorId) {
      // Already processed this exact error, skip
      return
    }

    // Mark this error as processed
    lastProcessedErrorRef.current = errorId
    const errorMessage = error?.message || ''
    const userFriendlyMessage = error?.userFriendlyMessage || ''
    const errorDetails = error?.details || []
    const status = error?.status || 0

    // Combine all error text for checking
    const allErrorText =
      `${errorMessage} ${userFriendlyMessage} ${errorDetails.map((d: ErrorDetail) => d.description || '').join(' ')}`.toLowerCase()

    // Check for customs limit exceeded error
    // Backend uses status 422 (UNPROCESSABLE_ENTITY) for entityNotCreated errors
    // and the error message contains "exceeds" and "customs limit"
    const isCustomsError =
      (status === 400 || status === 422) &&
      (allErrorText.includes('customs limit') ||
        allErrorText.includes('exceeds') ||
        allErrorText.includes('customs') ||
        errorDetails.some((detail: ErrorDetail) => detail.issue === 'customs_limit_exceeded'))

    if (isCustomsError) {
      const customsDetail = errorDetails.find(
        (detail: ErrorDetail) => detail.issue === 'customs_limit_exceeded' || detail.issue === 'creation_error'
      )
      const countryCode = recipientDetails?.country || 'Unknown'
      const country = getCountryByCode(countryCode)

      // Extract customs limit and total value from error message or userFriendlyMessage or details
      let customsLimitCents = 0
      let totalDeclaredValueCents = 0

      // Try to extract from error message or userFriendlyMessage
      const fullErrorText = `${errorMessage} ${userFriendlyMessage} ${customsDetail?.description || ''}`
      const dollarMatches = fullErrorText.match(/\$([\d,]+\.?\d*)/g)

      if (dollarMatches && dollarMatches.length >= 2) {
        // First match is usually the order total, second is the limit
        // But check which is larger to determine order
        const values = dollarMatches.map((match: string) => parseFloat(match.replace(/[$,]/g, '')) * 100)
        const largerValue = Math.max(...values)
        const smallerValue = Math.min(...values)

        // The larger value is the order total, smaller is the limit
        totalDeclaredValueCents = largerValue
        customsLimitCents = smallerValue
      } else if (customsDetail?.description) {
        // Try to extract from detail description
        const description = customsDetail.description
        const descMatches = description.match(/\$([\d,]+\.?\d*)/g)

        if (descMatches && descMatches.length >= 2) {
          const values = descMatches.map((match: string) => parseFloat(match.replace(/[$,]/g, '')) * 100)
          totalDeclaredValueCents = Math.max(...values)
          customsLimitCents = Math.min(...values)
        }
      }

      // Fallback: calculate from items if extraction failed
      if (totalDeclaredValueCents === 0) {
        totalDeclaredValueCents = items.reduce((sum, item) => sum + item.declaredValue * 100 * (item.quantity ?? 1), 0)
      }

      // If we still don't have a limit, try to get it from the backend response or use a default
      // For now, we'll show the error even without the exact limit
      if (customsLimitCents === 0) {
        // Try to find limit in error text (e.g., "limit of $800")
        const limitMatch = fullErrorText.match(/limit of \$([\d,]+\.?\d*)/i)
        if (limitMatch) {
          customsLimitCents = Math.round(parseFloat(limitMatch[1].replace(/[$,]/g, '')) * 100)
        }
      }

      setCustomsError({
        customsLimitCents,
        totalDeclaredValueCents,
        destinationCountry: country?.name || countryCode,
        exceededItems: items.map((item) => item.description)
      })
    } else if (
      // Check for network errors
      status === 0 ||
      errorMessage.toLowerCase().includes('network') ||
      errorMessage.toLowerCase().includes('connection') ||
      errorMessage.toLowerCase().includes('timeout') ||
      errorMessage.toLowerCase().includes('fetch') ||
      errorMessage.toLowerCase().includes('failed to fetch')
    ) {
      setCustomsError(null)
    } else {
      // Other errors - log for debugging
      // eslint-disable-next-line no-console
      console.error('Payment intent creation error:', {
        status,
        errorMessage,
        userFriendlyMessage,
        errorDetails,
        error
      })
      setCustomsError(null)
    }
  }, [paymentIntentError, items, recipientDetails])

  const handleContinueToPayment = async () => {
    if (!recipientDetails || !isRecipientFormValid || items.length === 0) {
      setShouldValidateRecipientForm(true)

      return
    }

    const isUSAddress = recipientDetails.country === 'US'

    if (!isUSAddress) {
      // USPS only validates US addresses — skip validation for international recipients
      handleCreatePaymentIntentRequest()

      return
    }

    const payload: ValidateUspsAddressRequestPayload = {
      request: {
        streetAddress: recipientDetails.streetAddress,
        city: recipientDetails.city,
        state: recipientDetails.stateProvince,
        ZIPCode: recipientDetails.postalCode
      },
      onSuccess: () => {
        setEnforceZipPlus4(false)
        setSuggestedZipPlus4(null)
        handleCreatePaymentIntentRequest()
      },
      onError: (errorResponse) => {
        const signal = parseZipPlusFourValidationSignal(errorResponse)

        if (signal.enforceZipPlus4) {
          setEnforceZipPlus4(true)
          setSuggestedZipPlus4(signal.suggestedZipPlus4)

          showSnackbar({
            message: 'We could not confirm this address. Please provide the full ZIP+4 (e.g. 12345-6789) to continue.',
            severity: 'error'
          })
        } else {
          showSnackbar({
            message:
              errorResponse?.userFriendlyMessage ||
              'Address could not be validated. Please verify the street address, city, state, and ZIP code.',
            severity: 'error'
          })
        }

        dispatch(resetValidateUspsAddress())
      }
    }

    dispatch(validateUspsAddress(payload))
  }

  // Payment intent creation handler: validate US address first, then create payment intent
  const handleCreatePaymentIntentRequest = async () => {
    if (!recipientDetails) {
      return
    }

    setConfirmedRecipientDetails(recipientDetails)
    setCustomsError(null)
    setCustomsErrorAlert(null)
    lastProcessedErrorRef.current = null

    const payload: CreateFulfillmentOrderPaymentIntentRequestPayload = {
      request: {
        package: packageDetails as FulfillmentOrderPackage,
        items: items.map((item) => ({
          photoUrl: item.photoPreview || undefined,
          description: item.description,
          quantity: item.quantity ?? 1,
          declaredValueCents: item.declaredValue * 100,
          fragile: item.fragile ?? false,
          repackagingAllowed: item.repackagingAllowed ?? false,
          specialInstructions: item.specialInstructions || null
        })),
        recipient: {
          firstName: recipientDetails.firstName,
          lastName: recipientDetails.lastName,
          email: recipientDetails.email,
          phone: recipientDetails.phone || undefined,
          streetAddress: recipientDetails.streetAddress,
          city: recipientDetails.city,
          stateProvince: recipientDetails.stateProvince,
          postalCode: recipientDetails.postalCode,
          country: recipientDetails.country,
          deliveryInstructions: recipientDetails.deliveryInstructions || null
        },
        uspsLocationId: selectedUSPSLocation?.locationID,
        returnUrl: `${window.location.origin}/${routes.createFulfillmentOrder.path.replace(/^\/+/, '')}`
      },
      onSuccess: () => {
        setActiveStep(2)
        window.scrollTo(0, 0)
      }
    }

    dispatch(resetCreateFulfillmentOrderPaymentIntent())
    dispatch(createFulfillmentOrderPaymentIntent(payload))
  }

  // Handle payment failure
  useEffect(() => {
    if (paymentFailure && activeStep === 2) {
      // Payment failed, stay on payment step
      window.scrollTo(0, 0)
    }
  }, [paymentFailure, activeStep])

  // Payment submission handler
  const handlePaymentSubmission = () => {
    if (recipientDetails && isRecipientFormValid && isPaymentFormValid && items.length > 0) {
      // Store order metadata in localStorage including photoPreview (data URL) for restoration after redirect
      // File objects can't be stored, but we can store data URLs and convert them back to File objects
      const orderSubmission = {
        packageDetails,
        items: items.map((item) => ({
          description: item.description,
          quantity: item.quantity,
          declaredValueCents: item.declaredValue * 100,
          currency: item.currency,
          photoPreview: item.photoPreview || null,
          photoName: item.photo?.name || 'item-photo.jpg'
        })),
        recipient: {
          firstName: recipientDetails.firstName,
          lastName: recipientDetails.lastName,
          email: recipientDetails.email,
          phone: recipientDetails.phone || undefined,
          streetAddress: recipientDetails.streetAddress,
          city: recipientDetails.city,
          stateProvince: recipientDetails.stateProvince,
          postalCode: recipientDetails.postalCode,
          country: recipientDetails.country,
          deliveryInstructions: recipientDetails.deliveryInstructions || null
        },
        uspsLocation: selectedUSPSLocation
      }

      localStorage.setItem(FULFILLMENT_ORDER_SUBMISSION_KEY, JSON.stringify(orderSubmission))
      paymentFormRef.current?.submitPayment()
    }
  }

  const handleStepClick = (step: number) => {
    // Allow backward navigation
    if (step < activeStep) {
      setActiveStep(step)
      // Reset validation trigger when navigating away from step 1
      if (step !== 1) {
        setShouldValidateRecipientForm(false)
      }
    } else if (step === activeStep + 1) {
      // Allow forward navigation only if current step is valid
      if (activeStep === 0 && items.length > 0 && isPackageDetailsValid) {
        setActiveStep(step)
      } else if (activeStep === 1 && isRecipientFormValid) {
        setActiveStep(step)
      }
    }
  }

  const handleContinueClick = () => {
    if (activeStep === 0 && items.length > 0 && isPackageDetailsValid) {
      setActiveStep(1)
      window.scrollTo(0, 0)
    }
  }

  const handleAdjustItems = () => {
    setActiveStep(0)
    setCustomsError(null)
    // Clear the Redux error state
    dispatch(resetCreateFulfillmentOrderPaymentIntent())
    const country = getCountryByCode(recipientDetails?.country || '')
    setCustomsErrorAlert(
      `Please remove items or reduce declared values to meet customs limits for ${country?.name || recipientDetails?.country || 'this country'}.`
    )
    window.scrollTo(0, 0)
  }

  const handleClearCustomsAlert = () => {
    setCustomsErrorAlert(null)
    // Also clear the Redux error when user dismisses the alert
    if (paymentIntentError) {
      dispatch(resetCreateFulfillmentOrderPaymentIntent())
    }
  }

  const handleCloseCustomsModal = () => {
    setCustomsError(null)
    // Clear the Redux error when modal is closed
    if (paymentIntentError) {
      dispatch(resetCreateFulfillmentOrderPaymentIntent())
    }
  }

  const renderStepHeader = () => {
    let headerTitle = ''
    let headerSubtitle = ''

    switch (activeStep) {
      case 0:
        headerTitle = 'Add Items'
        headerSubtitle = 'Add items you want to send to your recipient.'
        break

      case 1:
        headerTitle = 'Recipient Details'
        headerSubtitle = 'Provide recipient information for delivery.'
        break

      case 2:
        headerTitle = 'Review & Payment'
        headerSubtitle = 'Review your order and complete payment.'
        break

      default:
        return null
    }

    return (
      <Box sx={styles.stepHeader}>
        <Typography variant={isDesktop ? 'h4' : 'h6'} sx={styles.title}>
          {headerTitle}
        </Typography>

        {headerSubtitle && (
          <Typography variant="subtitle1" sx={styles.subtitle}>
            {headerSubtitle}
          </Typography>
        )}
      </Box>
    )
  }

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <>
            {customsErrorAlert && (
              <Alert severity="warning" sx={styles.customsErrorAlert} onClose={handleClearCustomsAlert}>
                {customsErrorAlert}
              </Alert>
            )}

            <Box sx={styles.itemsStepContainer}>
              <OverallOrderDetails
                packageDetails={packageDetails}
                onPackageDetailsChange={handlePackageDetailsChange}
              />

              <Box sx={styles.itemsStepInnerContainer}>
                {itemFormIds.map((itemFormId, itemIndex) => {
                  const existingItem = items.find((item) => item.id === itemFormId)

                  return (
                    <AddItemForm
                      key={itemFormId}
                      itemId={itemFormId}
                      itemNumber={itemIndex + 1}
                      showRemoveButton={itemFormIds.length > 1}
                      onRemove={() => handleRemoveItemCard(itemFormId)}
                      onChange={(isValid, updatedItem) => {
                        handleItemFormChange(itemFormId, isValid, updatedItem)
                        if (!isValid) handleClearCustomsAlert()
                      }}
                      initialValues={
                        existingItem
                          ? {
                              description: existingItem.description,
                              quantity: existingItem.quantity.toString(),
                              declaredValue: existingItem.declaredValue.toString(),
                              currency: existingItem.currency
                            }
                          : undefined
                      }
                      initialPhoto={existingItem?.photo || null}
                      initialPhotoPreview={existingItem?.photoPreview || ''}
                    />
                  )
                })}

                <Box
                  sx={
                    {
                      ...styles.addAnotherItemButton,
                      ...(!isLastItemFormValid ? styles.addAnotherItemButtonDisabled : {})
                    } as SxProps<Theme>
                  }
                  onClick={isLastItemFormValid ? handleAddAnotherItem : undefined}
                  role="button"
                  tabIndex={isLastItemFormValid ? 0 : -1}
                  onKeyDown={(event: React.KeyboardEvent) =>
                    event.key === 'Enter' && isLastItemFormValid && handleAddAnotherItem()
                  }
                  aria-disabled={!isLastItemFormValid}
                >
                  <Add sx={isLastItemFormValid ? styles.addAnotherItemIcon : styles.addAnotherItemIconDisabled} />

                  <Typography
                    sx={
                      {
                        ...styles.addAnotherItemText,
                        ...(!isLastItemFormValid ? styles.addAnotherItemTextDisabled : {})
                      } as SxProps<Theme>
                    }
                  >
                    Add Another Item
                  </Typography>
                </Box>
              </Box>
            </Box>
          </>
        )

      case 1:
        return (
          <>
            <RecipientDetails
              key={`recipient-details-${useUsPSLocationMode ? 'usps' : 'manual'}-${selectedUSPSLocation?.locationID ?? 'none'}`}
              onFormChange={handleRecipientDetailsChange}
              initialValues={recipientDetails || undefined}
              validateOnMount={shouldValidateRecipientForm}
              countriesAndCities={countriesAndCitiesData}
              addressFieldsReadonly={useUsPSLocationMode}
              enforceZipPlus4={enforceZipPlus4}
              suggestedZipPlus4={suggestedZipPlus4}
              additionalContent={
                useUsPSLocationMode ? (
                  <>
                    <Divider sx={styles.uspsDivider} />

                    <USPSLocationFinder
                      recipientZipCode={savedRecipientDetailsBeforeUsps?.postalCode ?? ''}
                      initialZipCode={savedRecipientDetailsBeforeUsps?.postalCode}
                      hideSearchForm
                      radiusMiles={defaultRadiusMiles()}
                      onLocationSelect={handleUspsLocationSelect}
                      selectedLocationID={selectedUSPSLocation?.locationID}
                      sectionTitle="Select USPS Location"
                    />
                  </>
                ) : undefined
              }
            />
          </>
        )

      case 2:
        return (
          <>
            {paymentFailure && (
              <Alert severity="error" sx={styles.paymentFailureAlert}>
                Payment was not successful. Please try again.
              </Alert>
            )}

            <Grid container spacing={4}>
              <Grid item xs={12} md={6.5}>
                <Box sx={styles.paymentDetailsWrapper}>
                  <PaymentDetails
                    mode="payment"
                    redirectPath={routes.createFulfillmentOrder.path}
                    paymentIntent={paymentIntentData || undefined}
                    customerSession={customerSessionData}
                    paymentIntentLoading={paymentIntentLoading}
                    handleCreatePaymentIntentRequest={handleCreatePaymentIntentRequest}
                    setIsPaymentFormValid={setIsPaymentFormValid}
                    setPaymentRequestLoading={setPaymentRequestLoading}
                    paymentFormRef={paymentFormRef}
                    autoCreatePaymentIntent={false}
                  />
                </Box>
              </Grid>

              {isDesktop && (
                <Grid item xs={12} md={5.5}>
                  <Box sx={styles.desktopOrderSummaryColumn}>
                    <FulfillmentRecipientCard
                      recipient={confirmedRecipientDetails || recipientDetails}
                      showEditIcon={true}
                      onEditClick={() => setActiveStep(1)}
                    />

                    <Overview
                      itemType="fulfillment"
                      title="Order Summary"
                      items={items}
                      paymentIntent={paymentIntent}
                      readonly={true}
                      fees={paymentIntent?.deliveryFee}
                      subtotal={items.reduce((sum, item) => sum + item.declaredValue * (item.quantity ?? 1), 0)}
                      total={paymentIntent?.totalAmount}
                    />
                  </Box>

                  <Box sx={styles.desktopSubmitButtonWrapper}>{getActiveButton()}</Box>
                </Grid>
              )}
            </Grid>
          </>
        )

      case 3: {
        const orderDescription =
          "Your fulfillment order has been submitted. We'll notify you when a traveler accepts your delivery request."

        const warehouseInfo = order
          ? {
              warehouseAddress: order.warehouseAddress || '',
              dropoffInstructions: order.dropoffInstructions || ''
            }
          : null

        const labelingInstructions = confirmedRecipientDetails
          ? {
              recipientName: `${confirmedRecipientDetails.firstName} ${confirmedRecipientDetails.lastName}`.trim(),
              recipientAddressLines: [
                confirmedRecipientDetails.streetAddress,
                `${confirmedRecipientDetails.city}, ${confirmedRecipientDetails.stateProvince} ${confirmedRecipientDetails.postalCode}`,
                getCountryByCode(confirmedRecipientDetails.country)?.name || confirmedRecipientDetails.country
              ].filter(Boolean)
            }
          : null

        return (
          <PaymentSubmittedSuccessfully
            title="Order Submitted Successfully!"
            description={orderDescription}
            orderReference={order?.orderId ? stripPrefixFromId(order.orderId) : null}
            labelingInstructions={labelingInstructions}
            additionalInfoTitle={warehouseInfo ? 'Dropoff Details' : undefined}
            button={{
              text: 'View Order',
              onClick: () => navigate(routes.fulfillmentOrders.path),
              buttonType: 'primary'
            }}
            secondaryButton={
              order
                ? {
                    buttonType: 'secondary',
                    text: 'Create New Order',
                    onClick: handleCreateNewOrder
                  }
                : undefined
            }
            loadingState={{ loading: orderLoading, text: 'Creating your order' }}
            success={order !== null}
            errorState={
              orderError
                ? {
                    title: 'Order Creation Failed',
                    subtitle: `Payment successful but order creation failed. Please contact support with your payment reference: ${paymentIntentParam || 'N/A'}`,
                    button: {
                      text: 'Retry Order Creation',
                      onClick: handleSubmitOrder
                    }
                  }
                : undefined
            }
            warehouseInfo={warehouseInfo}
          />
        )
      }

      default:
        return null
    }
  }

  const getActiveButton = () => {
    switch (activeStep) {
      case 0:
        return (
          <Button
            buttonType="primary"
            text={isDesktop ? 'Continue to Recipient Details' : 'Continue'}
            onClick={handleContinueClick}
            disabled={items.length === 0 || !isPackageDetailsValid}
            fullWidth
            sx={styles.continueButton}
          />
        )

      case 1:
        return (
          <Button
            buttonType="primary"
            text={isDesktop ? 'Continue to Payment' : 'Continue'}
            onClick={handleContinueToPayment}
            disabled={!isRecipientFormValid || paymentIntentLoading || validateAddressLoading}
            loading={paymentIntentLoading || validateAddressLoading}
            fullWidth
            sx={styles.continueButton}
          />
        )

      case 2:
        return (
          <Button
            buttonType="primary"
            text={isDesktop ? 'Submit Order' : 'Submit'}
            onClick={handlePaymentSubmission}
            disabled={!isPaymentFormValid || paymentRequestLoading}
            loading={paymentRequestLoading}
            fullWidth
            sx={styles.continueButton}
          />
        )

      default:
        return null
    }
  }

  const renderContent = () => {
    const isOverviewSectionVisible = activeStep !== 3 && !paymentSuccess

    return (
      <Stepper
        activeStep={activeStep}
        steps={submissionSteps}
        showStepper={true}
        onStepClick={handleStepClick}
        stepperWidth="70%"
        showLeftArrow={activeStep !== 3}
        showRightArrow={activeStep !== 3}
      >
        {renderStepHeader()}

        <Grid container spacing={isOverviewSectionVisible && activeStep !== 2 ? 4 : 0} sx={styles.content}>
          <Grid item xs={12} md={isOverviewSectionVisible && activeStep !== 2 ? 8 : 12}>
            <Box sx={styles.stepContent}>{renderStepContent()}</Box>
          </Grid>

          {isOverviewSectionVisible && isDesktop && activeStep !== 2 && (
            <Grid item xs={12} md={4}>
              <Box sx={styles.overviewWrapper}>
                {activeStep !== 1 && (
                  <FulfillmentRecipientCard
                    recipient={confirmedRecipientDetails || recipientDetails}
                    showEditIcon={true}
                    onEditClick={() => setActiveStep(1)}
                  />
                )}

                <Overview
                  itemType="fulfillment"
                  title="Order Summary"
                  items={items}
                  packageDetails={packageDetails}
                  paymentIntent={paymentIntent}
                  readonly={activeStep !== 0}
                  onEditItem={
                    activeStep === 0 && itemsStepEditHandler
                      ? (index) => {
                          itemsStepEditHandler(index)
                        }
                      : undefined
                  }
                  onRemoveItem={activeStep === 0 ? handleRemoveItem : undefined}
                  showEditRemove={activeStep === 0}
                  fees={paymentIntent?.deliveryFee}
                  subtotal={items.reduce((sum, item) => sum + item.declaredValue * (item.quantity ?? 1), 0)}
                  total={paymentIntent?.totalAmount}
                />

                {getActiveButton()}
              </Box>
            </Grid>
          )}
        </Grid>

        {isOverviewSectionVisible && (
          <ResponsiveDisplayWrapper isDesktop={false}>
            <Box sx={styles.overviewContainer}>
              <Box sx={styles.overviewWrapper}>
                {activeStep !== 1 && (confirmedRecipientDetails || recipientDetails) && (
                  <>
                    <FulfillmentRecipientCard
                      recipient={confirmedRecipientDetails || recipientDetails}
                      showEditIcon={true}
                      onEditClick={() => setActiveStep(1)}
                    />

                    <Divider sx={styles.divider} />
                  </>
                )}

                <Overview
                  itemType="fulfillment"
                  title="Order Summary"
                  items={items}
                  packageDetails={packageDetails}
                  paymentIntent={paymentIntent}
                  readonly={activeStep !== 0}
                  onEditItem={
                    activeStep === 0 && itemsStepEditHandler
                      ? (index) => {
                          itemsStepEditHandler(index)
                          window.scrollTo({ top: 0, behavior: 'smooth' })
                        }
                      : undefined
                  }
                  onRemoveItem={activeStep === 0 ? handleRemoveItem : undefined}
                  showEditRemove={activeStep === 0}
                  fees={paymentIntent?.deliveryFee}
                  subtotal={items.reduce((sum, item) => sum + item.declaredValue * (item.quantity ?? 1), 0)}
                  total={paymentIntent?.totalAmount}
                  getActiveButton={() => getActiveButton()}
                />
              </Box>
            </Box>
          </ResponsiveDisplayWrapper>
        )}
      </Stepper>
    )
  }

  return (
    <Box sx={styles.container}>
      {renderContent()}

      <CustomsLimitErrorModal
        open={customsError !== null}
        error={customsError}
        onClose={handleCloseCustomsModal}
        onAdjustItems={handleAdjustItems}
      />
    </Box>
  )
}

export default CreateFulfillmentOrder
